import { formatPrice } from '../../utils-module.js';

export function ecomIsNodeTitleHasCorrectMode(node, isVip) {
  return isNodeTitleHasCorrectMode(node, isVip);
}

function isNodeTitleHasCorrectMode(node, isVip) {
  const mode = String(isVip ?? '')
    .trim()
    .toLowerCase();
  if (!mode) {
    return false;
  }

  const title = String(node?.title ?? '')
    .trim()
    .toLowerCase();
  if (!title) {
    return false;
  }

  if (title === 'default title' || title === 'unlock_media') {
    return true;
  }

  if (Number.isFinite(Number(title))) {
    return true;
  }

  // Accept common separators used in variant titles: slash, comma, pipe, or extra whitespace.
  const tokens = title
    .split(/[/|,]/g)
    .map((token) => token.trim())
    .filter(Boolean);

  return tokens.includes(mode);
}

export function normalizeProductId(id) {
  return `gid://shopify/Product/${id}`;
}

export function normalizeCollectionId(id) {
  return `gid://shopify/Collection/${id}`;
}

export function ecomFormatDecimals(value, digits = 2) {
  return (Math.round(value * 100) / 100).toFixed(digits);
}

export function ecomFormatCurrency(currencyCode) {
  if (currencyCode === 'USD') return '$';
  return currencyCode;
}

export function ecomFormatPrice(amount = 0, currencyCode = 'USD') {
  return `${ecomFormatCurrency(currencyCode)}${ecomFormatDecimals(amount)}`;
}

export function ecomGetLocalisedObjectImage(obj) {
  const lang = ecomExtractPageLang(localStorage.getItem('i18nextLng'));
  const imageListJson = obj?.image_list_json || obj?.imageListJson;
  if (!imageListJson) return undefined;
  try {
    const imageList = JSON.parse(imageListJson);
    return (
      imageList.find(({ lang: imageLang }) => imageLang === lang)?.url ??
      imageList.find(({ lang: imageLang }) => imageLang === 'en')?.url
    );
  } catch {
    return undefined;
  }
}

export function ecomExtractPageLang(localeLang) {
  if (typeof localeLang !== 'string') return 'en';
  const parts = localeLang.split('-');
  return parts[0] || 'en';
}

export function ecomInternalProductToVariant(product) {
  return {
    id: product.id,
    productTitle: product.name,
    imgUrl: ecomGetLocalisedObjectImage(product),
    printPool: product.print_pull,
    productPurpose: product.product_purpose,
    partialUnlockTags: product.partial_unlock_tags,
    eventName: product.event_code,
  };
}

function getExternalProductsFromCollection(externalProductId, externalProductList) {
  const externalId = normalizeProductId(externalProductId);
  for (let i = 0; i < externalProductList.length; i += 1) {
    if (externalProductList[i].node.id === externalId) return externalProductList[i];
  }
  return null;
}

function getProductVariant(product, isVip) {
  const prodVariantList = product.variants?.edges.map((edge) => edge.node) || [];
  if (prodVariantList.length < 1) return null;
  for (let k = 0; k < prodVariantList.length; k += 1) {
    if (isNodeTitleHasCorrectMode(prodVariantList[k], isVip)) {
      return prodVariantList[k];
    }
  }
  return null;
}

function getExternalProductVariantNodeForCollection(productId, externalProductEdges, isVip) {
  const product = getExternalProductsFromCollection(productId, externalProductEdges);
  const variantEdges = product?.node?.variants?.edges;
  if (!variantEdges) return null;
  for (let i = 0; i < variantEdges.length; i += 1) {
    if (isNodeTitleHasCorrectMode(variantEdges[i].node, isVip)) {
      return variantEdges[i].node;
    }
  }
  return null;
}

export function ecomGetComboProductsIds(collectionList) {
  return collectionList.reduce((comboIds, item) => {
    try {
      const productJson = JSON.parse(item.collection_product_json);
      if (productJson?.comboExternalId) {
        comboIds.push(productJson.comboExternalId);
      }
      if (item.is_addon_collection === 1) {
        const products = item.products || [];
        products.forEach((prod) => {
          if (prod?.collection_id !== null) {
            comboIds.push(prod.external_product_id);
          }
        });
      }
    } catch (e) {
      /* ignore */
    }
    return comboIds;
  }, []);
}

export function ecomGetCollComboVariant(collectionFullData, isVip) {
  const occoCollData = collectionFullData.occoCollectionData;
  const externalCollData = collectionFullData?.collectionAndComboData?.collectionData;
  const externalCollComboData = collectionFullData?.collectionAndComboData?.collectionComboData;
  const collectionProducts = JSON.parse(occoCollData.collection_product_json, {});

  const variantProdPack = getProductVariant(externalCollComboData.product, isVip);
  const newProdCount = {};
  for (let i = 0; i < collectionProducts.collectionProductInfo.length; i += 1) {
    const prodCountInfo = collectionProducts.collectionProductInfo[i];
    newProdCount[prodCountInfo.productId] = prodCountInfo.amount;
  }

  const variantArr = [];
  const { products } = occoCollData;
  const { edges } = externalCollData.products;
  let prodGroup = 0;
  for (let t = 0; t < products.length; t += 1) {
    const product = products[t];
    prodGroup += 1;
    const isResortFee = product.product_purpose === 'resort_fee';
    const isMediaSelectProduct =
      product?.productPurpose === 'printable' ||
      product?.productPurpose === 'partial_unlock_media' ||
      product?.product_purpose === 'printable' ||
      product?.product_purpose === 'partial_unlock_media';

    const extVariantNode = getExternalProductVariantNodeForCollection(product.external_product_id, edges, isVip);
    const variantId = extVariantNode?.id ?? null;
    const lineSku = extVariantNode?.sku || product.sku || null;
    const newVariant = ecomInternalProductToVariant(product);
    if (newProdCount[product.id] && isMediaSelectProduct) {
      for (let i = 0; i < newProdCount[product.id]; i += 1) {
        const populatedVariant = { ...newVariant };
        populatedVariant.variantId = variantId;
        populatedVariant.sku = lineSku;
        populatedVariant.pid = `${prodGroup}_${i}`;
        populatedVariant.printPool = product.print_pull;
        populatedVariant.shippingPrintPool = product.shipping_print_pool;
        populatedVariant.requireTags = product.require_tags;
        populatedVariant.denyTags = product.deny_tags;
        variantArr.push(populatedVariant);
      }
    } else {
      if (isResortFee) {
        const { price } = variantProdPack;
        const { currency } = product;
        const percent = isVip === '1' ? product?.vip_price || 0.0 : product?.non_vip_price || 0.0;
        let fee = (price * percent) / 100;
        fee = Math.round(fee * 100) / 100;
        newVariant.productTitle = `${product.name} (${percent}% included) - ${formatPrice({
          amount: fee,
          currencyCode: currency,
        })}`;
        newVariant.isResortFee = true;
      } else {
        newVariant.productTitle = product.name;
      }
      newVariant.variantId = variantId;
      newVariant.sku = lineSku;
      newVariant.pid = `${prodGroup}_0`;
      newVariant.requireTags = product.require_tags;
      newVariant.denyTags = product.deny_tags;
      newVariant.partialUnlockTags = product.partial_unlock_tags;
      variantArr.push(newVariant);
    }
  }
  variantProdPack.variantArr = variantArr;
  return variantProdPack;
}

export function ecomGetCollectionPackageVariantID(collectionPackage) {
  if (collectionPackage?.id?.includes('gid://')) {
    return collectionPackage.id;
  }
  return `gid://shopify/ProductVariant/${collectionPackage.id}`;
}

export function ecomGetProductsFromCollection(collection, productMap) {
  const products = [];
  try {
    const prodList = JSON.parse(collection.collection_product_json);
    prodList.forEach(({ productId }) => {
      const prodData = productMap.get(productId);
      if (prodData) {
        products.push(prodData);
      }
    });
  } catch (err) {
    /* ignore */
  }
  return products;
}

export function ecomGetProductCountInCollection(collection, productId) {
  const { collectionProductInfo } = JSON.parse(collection.collection_product_json);
  let count = 0;
  for (let p = 0; p < collectionProductInfo.length; p += 1) {
    const pid = collectionProductInfo[p];
    if (pid.productId === productId) {
      count = pid.amount;
    }
  }
  return count;
}
